/**
 * Project Details Form Change Tracking Utilities
 * Validates and tracks changes in project, test of change, and test data forms
 */

/**
 * Interface for project form data structure
 */
export interface ProjectFormData {
  projectName: string;
  valueCompass: (string | number)[];
  status: { id: string | number; label: string; value: string | number };
  focusArea: { id: string | number; label: string; value: string | number };
  startDate?: string;
  completedDate?: string;
  smartGoal: string;
  performanceMetric: { id: string | number; label: string; value: string | number };
  pspGoal: string;
  dataSource: { id: string | number; label: string; value: string | number };
  baseline: string;
  threshold: string;
  target: string;
  stretch: string;
  metricDescription: string;
  dataSourceDescription: string;
  toolsSelected: (string | number)[];
  resultSummary: { id: string | number; label: string; value: string | number };
  resultDescription: string;
  annualRoi: string;
  learnings: string;
  numerator: string;
  denominator: string;
  calculatedValue: string;
  additionalVar: string;
  teamDocs: string;
}

/**
 * Interface for test of change form data structure
 */
export interface TestOfChangeFormData {
  tocTitle: string;
  tocText: string;
  outcome: string;
  selectedIdeaSource: { id: string | number; label: string; value: string | number };
  selectedRating: { id: string | number; label: string; value: string | number };
}

/**
 * Interface for test data form structure
 */
export interface TestDataFormData {
  testDataDate: string;
  selectedShift: { id: string | number; label: string; value: string | number };
  numeratorName: string;
  denomenatorName: string;
  notes: string;
  outSiderVarVal: string;
}

/**
 * Check if two values are equal (handles strings, numbers, arrays, and objects)
 * @param a - First value
 * @param b - Second value
 * @returns True if values are equal
 */
const areValuesEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  
  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => String(item) === String(b[index]));
  }
  
  // Handle objects (dropdowns)
  if (typeof a === 'object' && typeof b === 'object') {
    const aValue = String(a?.value ?? a?.id ?? '');
    const bValue = String(b?.value ?? b?.id ?? '');
    return aValue === bValue;
  }
  
  // Handle primitives
  return String(a) === String(b);
};

/**
 * Check if project form has changed from initial values
 * @param currentData - Current project form data
 * @param initialData - Initial project form data
 * @returns True if any field has changed
 */
export const hasProjectFormChanged = (
  currentData: Partial<ProjectFormData>,
  initialData: Partial<ProjectFormData>,
): boolean => {
  const fieldsToCheck: (keyof ProjectFormData)[] = [
    'projectName',
    'valueCompass',
    'status',
    'focusArea',
    'startDate',
    'completedDate',
    'smartGoal',
    'performanceMetric',
    'pspGoal',
    'dataSource',
    'baseline',
    'threshold',
    'target',
    'stretch',
    'metricDescription',
    'dataSourceDescription',
    'toolsSelected',
    'resultSummary',
    'resultDescription',
    'annualRoi',
    'learnings',
    'numerator',
    'denominator',
    'calculatedValue',
    'additionalVar',
    'teamDocs',
  ];

  return fieldsToCheck.some((field) => {
    const currentValue = currentData[field];
    const initialValue = initialData[field];
    return !areValuesEqual(currentValue, initialValue);
  });
};

/**
 * Check if test of change form has changed from initial values
 * @param currentData - Current test of change form data
 * @param initialData - Initial test of change form data
 * @returns True if any field has changed
 */
export const hasTestOfChangeFormChanged = (
  currentData: Partial<TestOfChangeFormData>,
  initialData: Partial<TestOfChangeFormData>,
): boolean => {
  const fieldsToCheck: (keyof TestOfChangeFormData)[] = [
    'tocTitle',
    'tocText',
    'outcome',
    'selectedIdeaSource',
    'selectedRating',
  ];

  return fieldsToCheck.some((field) => {
    const currentValue = currentData[field];
    const initialValue = initialData[field];
    return !areValuesEqual(currentValue, initialValue);
  });
};

/**
 * Check if test data form has changed from initial values
 * @param currentData - Current test data form data
 * @param initialData - Initial test data form data
 * @returns True if any field has changed
 */
export const hasTestDataFormChanged = (
  currentData: Partial<TestDataFormData>,
  initialData: Partial<TestDataFormData>,
): boolean => {
  const fieldsToCheck: (keyof TestDataFormData)[] = [
    'testDataDate',
    'selectedShift',
    'numeratorName',
    'denomenatorName',
    'notes',
    'outSiderVarVal',
  ];

  return fieldsToCheck.some((field) => {
    const currentValue = currentData[field];
    const initialValue = initialData[field];
    return !areValuesEqual(currentValue, initialValue);
  });
};

/**
 * Create a snapshot of project form data for initial comparison
 * @param data - Project form data
 * @returns Snapshot of the data
 */
export const createProjectFormSnapshot = (data: Partial<ProjectFormData>): Partial<ProjectFormData> => {
  return {
    projectName: data.projectName ?? '',
    valueCompass: data.valueCompass ? [...data.valueCompass] : [],
    status: data.status ? { ...data.status } : { id: '', label: '', value: '' },
    focusArea: data.focusArea ? { ...data.focusArea } : { id: '', label: '', value: '' },
    startDate: data.startDate ?? undefined,
    completedDate: data.completedDate ?? undefined,
    smartGoal: data.smartGoal ?? '',
    performanceMetric: data.performanceMetric ? { ...data.performanceMetric } : { id: '', label: '', value: '' },
    pspGoal: data.pspGoal ?? '',
    dataSource: data.dataSource ? { ...data.dataSource } : { id: '', label: '', value: '' },
    baseline: data.baseline ?? '',
    threshold: data.threshold ?? '',
    target: data.target ?? '',
    stretch: data.stretch ?? '',
    metricDescription: data.metricDescription ?? '',
    dataSourceDescription: data.dataSourceDescription ?? '',
    toolsSelected: data.toolsSelected ? [...data.toolsSelected] : [],
    resultSummary: data.resultSummary ? { ...data.resultSummary } : { id: '', label: '', value: '' },
    resultDescription: data.resultDescription ?? '',
    annualRoi: data.annualRoi ?? '',
    learnings: data.learnings ?? '',
    numerator: data.numerator ?? '',
    denominator: data.denominator ?? '',
    calculatedValue: data.calculatedValue ?? '',
    additionalVar: data.additionalVar ?? '',
    teamDocs: data.teamDocs ?? '',
  };
};

/**
 * Create a snapshot of test of change form data for initial comparison
 * @param data - Test of change form data
 * @returns Snapshot of the data
 */
export const createTestOfChangeFormSnapshot = (
  data: Partial<TestOfChangeFormData>,
): Partial<TestOfChangeFormData> => {
  return {
    tocTitle: data.tocTitle ?? '',
    tocText: data.tocText ?? '',
    outcome: data.outcome ?? '',
    selectedIdeaSource: data.selectedIdeaSource ? { ...data.selectedIdeaSource } : { id: '', label: '', value: '' },
    selectedRating: data.selectedRating ? { ...data.selectedRating } : { id: '', label: '', value: '' },
  };
};

/**
 * Create a snapshot of test data form data for initial comparison
 * @param data - Test data form data
 * @returns Snapshot of the data
 */
export const createTestDataFormSnapshot = (
  data: Partial<TestDataFormData>,
): Partial<TestDataFormData> => {
  return {
    testDataDate: data.testDataDate ?? '',
    selectedShift: data.selectedShift ? { ...data.selectedShift } : { id: '', label: '', value: '' },
    numeratorName: data.numeratorName ?? '',
    denomenatorName: data.denomenatorName ?? '',
    notes: data.notes ?? '',
    outSiderVarVal: data.outSiderVarVal ?? '',
  };
};
